import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { Layout } from "@/components/Layout";
import { unsubscribeNewsletterFn } from "@/lib/newsletter.functions";
import { z } from "zod";

const searchSchema = z.object({ token: z.string().optional() });

export const Route = createFileRoute("/uutiskirje/peruuta")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Peruuta uutiskirje | Montessori24" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const { token } = useSearch({ from: "/uutiskirje/peruuta" });
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ok" }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  const onConfirm = async () => {
    if (!token) {
      setState({ kind: "error", message: "Linkistä puuttuu tunniste." });
      return;
    }
    setState({ kind: "loading" });
    try {
      const res = await unsubscribeNewsletterFn({ data: { token } });
      if (res.ok) setState({ kind: "ok" });
      else setState({ kind: "error", message: res.error });
    } catch {
      setState({ kind: "error", message: "Peruutus epäonnistui." });
    }
  };

  return (
    <Layout>
      <section className="container-prose py-24 md:py-32">
        <div className="mx-auto max-w-xl text-center">
          {state.kind === "ok" ? (
            <>
              <h1 className="font-display text-4xl text-foreground">Tilaus peruutettu</h1>
              <p className="mt-4 text-muted-foreground">
                Emme lähetä sinulle enää uutiskirjeitä. Tervetuloa takaisin milloin tahansa.
              </p>
              <Link to="/" className="mt-8 inline-flex rounded-full border border-border px-6 py-3 text-sm">
                Palaa etusivulle
              </Link>
            </>
          ) : state.kind === "error" ? (
            <>
              <h1 className="font-display text-4xl text-foreground">Peruutus epäonnistui</h1>
              <p className="mt-4 text-muted-foreground">{state.message}</p>
            </>
          ) : (
            <>
              <h1 className="font-display text-4xl text-foreground">Peruuta uutiskirje</h1>
              <p className="mt-4 text-muted-foreground">
                Vahvistamalla peruutuksen et saa enää uutiskirjeitämme.
              </p>
              <button
                type="button"
                onClick={onConfirm}
                disabled={state.kind === "loading"}
                className="mt-8 inline-flex rounded-full bg-clay px-6 py-3 text-sm font-medium text-white disabled:opacity-70"
              >
                {state.kind === "loading" ? "Peruutetaan…" : "Vahvista peruutus"}
              </button>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}